import React from 'react'
import { Tag } from 'primereact/tag'
import { Badge } from 'primereact/badge'
import { ShippingOrder } from '../../../DeShippingTableWrapper'
import { dateTemplate } from '../../shared/DateTemplate'

interface OrderCardProps {
  order: ShippingOrder
  onOrderClick?: (order: ShippingOrder) => void
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, onOrderClick }) => {

  // Truncate email if too long
  const truncateEmail = (email: string, maxLength: number = 35) => {
    if (!email || email.trim() === '') {
      return 'N/A'
    }
    return email.length > maxLength ? `${email.substring(0, maxLength)}...` : email
  }

  const cardStyle = {
    width: '100%',
    maxWidth: '800px',
    minWidth: '600px',
    padding: '1.5rem',
    margin: '0 auto 1rem auto',
    cursor: onOrderClick ? 'pointer' : 'default',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: '1rem'
  }

  const orderNameStyle = {
    flex: '1 1 auto',
    marginRight: '2rem'
  }

  const dateStyle = {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }

  const mainContentStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
    width: '100%'
  }

  const customerSectionStyle = {
    flex: '0 0 250px',
    minWidth: '200px'
  }

  const statusSectionStyle = {
    flex: '1 1 auto',
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  }

  const priceSectionStyle = {
    flex: '0 0 150px',
    textAlign: 'right' as const,
    minWidth: '120px'
  }

  const handleCardClick = () => {
    if (onOrderClick) {
      onOrderClick(order)
    }
  }

  return (
    <div 
      key={order.order_id} 
      className={`order-card border-round-md surface-card shadow-1 ${onOrderClick ? 'order-card-clickable' : ''}`}
      style={cardStyle}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        if (onOrderClick) {
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
        }
      }}
      onMouseLeave={(e) => {
        if (onOrderClick) {
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      {/* Header Section - Order Name and Date properly distributed */}
      <div style={headerStyle}>
        <div style={orderNameStyle}>
          <h5 className="order-title m-0 text-900 font-semibold">{order.order_name}</h5>
        </div>
        <div style={dateStyle} className="text-600">
          <i className="pi pi-calendar text-sm"></i>
          <span className="text-sm">{dateTemplate(order)}</span>
        </div>
      </div>

      {/* Main Content Row - Customer, Status, and Price spread horizontally */}
      <div style={mainContentStyle}>
        {/* Customer Info */}
        <div className="flex align-items-center gap-2" style={customerSectionStyle}>
          <i className="pi pi-user text-600" style={{ marginRight: '0.5rem' }}></i>
          <span className="customer-email text-700" title={order.email}>
            {truncateEmail(order.email)}
          </span>
        </div>

        {/* Status Tags */}
        <div style={statusSectionStyle}>
          <Tag 
            value={`${order.assigned_items} assigned`}
            icon="pi pi-check"
            className="assigned-tag"
            severity="success"
          />
          {order.unassigned_items > 0 && (
            <Tag 
              value={`${order.unassigned_items} unassigned`}
              icon="pi pi-exclamation-triangle"
              className="unassigned-tag"
              severity="warning"
            />
          )}
        </div>

        {/* Price and Item Count */}
        <div style={priceSectionStyle}>
          <div className="text-900 font-semibold text-lg">
            ${order.order_subtotal.toFixed(2)}
          </div>
          <div className="text-600 text-sm">
            {order.total_items} item{order.total_items !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      
      {/* Click Indicator */}
      {onOrderClick && (
        <div className="text-center mt-2">
          <small className="text-500">
            <i className="pi pi-hand-pointer mr-1" style={{ fontSize: '0.75rem', marginRight: '4px' }}></i>
            Click for details
          </small>
        </div>
      )}
    </div>
  )
} 